// API wrappers（呼叫 Netlify Functions）
// Netlify Functions v1 不支援 subfolder，檔案與 URL 都是扁平化 group-action
// see architecture.md#L280

import { getSupabase } from './supabase-client.js';
import { apiUrl } from './api-base.js';

class ApiError extends Error {
  constructor({ code, message_zh, message_en, status }) {
    super(message_zh || message_en || code);
    this.code = code;
    this.message_zh = message_zh;
    this.message_en = message_en;
    this.status = status;
  }
}

async function authHeaders() {
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─────────── B5 冪等重試核心 ───────────
// 重點：冪等鍵在「建好 init 後」就固定，所有重試沿用同一把（init 物件不變）。
// 之前每次呼叫才產一把、且沒有重試 → 網路抖動時 caller 只能重打 = 重產新 key
// → server 端冪等失效 → 重複建單/收款。現在自動重試沿用同 key，server 端
// withIdempotency / B1 pending lock 會 replay 既有結果，不會重複。
const MAX_ATTEMPTS = 3;
// 寫入：只在「幾乎確定沒處理完」的訊號重試（gateway / 逾時）。
//   故意排除 500（server 已進入處理、結果未知）、409（衝突，重試無益）、
//   429（限流，Retry-After 是秒級，250ms 重試必再撞牆 → 直接冒給 UI 顯示「操作過於頻繁」）。
const WRITE_RETRY_STATUS = new Set([408, 425, 502, 503, 504]);
// 唯讀 GET：再加 500，重試無副作用。429 同上不重試。
const GET_RETRY_STATUS = new Set([408, 425, 500, 502, 503, 504]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * 帶重試的 fetch。init（含 headers/body，含固定的 Idempotency-Key）在外層建好後
 * 傳進來，重試期間完全沿用 → 冪等鍵穩定。
 * @param {string} url
 * @param {RequestInit} init
 * @param {Set<number>} retryStatus 可重試的 HTTP 狀態碼
 */
async function fetchWithRetry(url, init, retryStatus) {
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(url, init);
      if (retryStatus.has(res.status) && attempt < MAX_ATTEMPTS) {
        await sleep(250 * 2 ** (attempt - 1));  // 250ms, 500ms
        continue;
      }
      return res;
    } catch (netErr) {
      // 網路層失敗（離線 / DNS / 連線中斷）→ 同 key 重試
      lastErr = netErr;
      if (attempt < MAX_ATTEMPTS) {
        await sleep(250 * 2 ** (attempt - 1));
        continue;
      }
      throw netErr;
    }
  }
  throw lastErr;
}

async function request(path, opts = {}) {
  const method = opts.method || 'GET';
  const isWrite = method !== 'GET' && method !== 'HEAD';
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
    ...(opts.headers || {}),
  };

  // 冪等鍵：所有寫入操作一律帶一把，在此產一次、重試沿用。
  // caller 仍可用 opts.idempotencyKey 傳入綁定特定意圖的 key（例如綁購物車）。
  if (isWrite && !headers['Idempotency-Key']) {
    headers['Idempotency-Key'] = opts.idempotencyKey || crypto.randomUUID();
  }

  const init = {
    ...opts,
    method,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  };
  const retryStatus = isWrite ? WRITE_RETRY_STATUS : GET_RETRY_STATUS;
  const res = await fetchWithRetry(apiUrl(`/.netlify/functions${path}`), init, retryStatus);

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new ApiError({ ...data.error, status: res.status });
  }
  return data;
}

// ─────────── Public (QR customer, no auth) ───────────
/**
 * 顧客掃 QR 後拿公開菜單
 * @param {string} slug tenant slug
 */
export async function fetchPublicMenu(slug) {
  const res = await fetch(apiUrl(`/.netlify/functions/public-menu?slug=${encodeURIComponent(slug)}`));
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

/**
 * 顧客匿名送單
 * @param {object} opts
 * @param {string} [opts.qrToken] C8 方案 A：session token（typ='sess'），若 tenant.require_qr_token=true 必帶
 */
export async function createPublicOrder({ slug, tableNumber, items, customerNote, customerContact, qrToken }) {
  // key 在 init 內固定 → fetchWithRetry 重試沿用同把 → server withIdempotency 會 replay
  const res = await fetchWithRetry(apiUrl('/.netlify/functions/public-order-create'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': crypto.randomUUID(),
    },
    body: JSON.stringify({
      slug,
      table_number: tableNumber,
      items,
      customer_note: customerNote,
      customer_contact: customerContact,
      ...(qrToken ? { qr_token: qrToken } : {}),
    }),
  }, WRITE_RETRY_STATUS);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

/**
 * C8 方案 A：用長期 QR token 換 5-min session token
 * @param {string} qrToken 長期 QR token（typ='qr'）
 * @returns {Promise<{ session_token: string, table_number: string, expires_at: string }>}
 */
export async function exchangeQrSession(qrToken) {
  const res = await fetch(apiUrl('/.netlify/functions/qr-session'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ qr_token: qrToken }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

// ─────────── Menu ───────────
/**
 * @param {object} opts
 * @param {string} [opts.storeId]
 * @param {boolean} [opts.includeInactive] - owner 編輯視圖需設 true（看已停用的分類/品項）
 */
export async function fetchMenu({ storeId, includeInactive = false } = {}) {
  try {
    const params = new URLSearchParams();
    if (storeId) params.set('store_id', storeId);
    if (includeInactive) params.set('all', '1');
    const qs = params.toString() ? `?${params}` : '';
    return await request(`/menu-list${qs}`);
  } catch (err) {
    console.warn('[api] fetchMenu failed, returning empty', err);
    return { categories: [], items: [] };
  }
}

// 效能 #3：menu 修改後主動清 SW menu cache，避免 60 秒 max-age 期間
// owner 看不到自己改的結果。dev 環境無 SW 時此 fn 是 no-op。
async function invalidateMenuCache() {
  if (typeof caches === 'undefined') return;
  try { await caches.delete('pos-menu'); } catch { /* no-op */ }
}

/**
 * 2026-04-23 Codex B1 修 · cost 專用 endpoint（owner/manager only，no-cache）
 * 跟 fetchMenu 分離避免 cross-role SW cache 洩漏
 * @returns {Promise<{ costs: Record<string, number> }>}
 */
export async function fetchMenuItemCosts() {
  return request('/menu-items-cost-list');
}

export async function upsertCategory(payload) {
  const res = await request('/menu-category-upsert', {
    method: 'POST', body: payload, idempotencyKey: crypto.randomUUID(),
  });
  await invalidateMenuCache();
  return res;
}

export async function deleteCategory(id) {
  const res = await request('/menu-category-delete', {
    method: 'POST', body: { id }, idempotencyKey: crypto.randomUUID(),
  });
  await invalidateMenuCache();
  return res;
}

export async function upsertMenuItem(payload) {
  const res = await request('/menu-item-upsert', {
    method: 'POST', body: payload, idempotencyKey: crypto.randomUUID(),
  });
  await invalidateMenuCache();
  return res;
}

export async function deleteMenuItem(id) {
  const res = await request('/menu-item-delete', {
    method: 'POST', body: { id }, idempotencyKey: crypto.randomUUID(),
  });
  await invalidateMenuCache();
  return res;
}

// ─────────── Store Tables（桌位）───────────
export async function listStoreTables({ includeInactive = false } = {}) {
  const qs = includeInactive ? '?include_inactive=1' : '';
  try {
    return await request(`/store-tables-list${qs}`);
  } catch (err) {
    console.warn('[api] listStoreTables failed, returning empty', err);
    return { tables: [] };
  }
}

export async function upsertStoreTable(payload) {
  return request('/store-tables-upsert', { method: 'POST', body: payload });
}

export async function deleteStoreTable(id) {
  return request('/store-tables-delete', { method: 'POST', body: { id } });
}

// ─────────── Notification Routes（站點分流通知）───────────
export async function listNotificationRoutes() {
  try {
    return await request('/notification-routes-list');
  } catch (err) {
    console.warn('[api] listNotificationRoutes failed, using empty', err);
    return { routes: [] };
  }
}

export async function upsertNotificationRoute(payload) {
  return request('/notification-routes-upsert', { method: 'POST', body: payload });
}

// ─────────── Orders ───────────
export async function createOrder({ storeId, items, tableNumber, customerNote, paymentMethod }) {
  const idempotencyKey = crypto.randomUUID();
  return request('/orders-create', {
    method: 'POST',
    idempotencyKey,
    body: {
      store_id: storeId,
      items,
      table_number: tableNumber,
      customer_note: customerNote,
      payment_method: paymentMethod,
    },
  });
}

export async function listOrders({ status, limit = 50, scope } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (scope) params.set('scope', scope);
  params.set('limit', limit);
  return request(`/orders-list?${params}`);
}

export async function updateOrderStatus(orderId, status) {
  return request(`/orders-update-status`, {
    method: 'PATCH',
    body: { order_id: orderId, status },
  });
}

export async function deleteOrder(orderId) {
  return request('/orders-delete', {
    method: 'POST',
    body: { order_id: orderId },
  });
}

/**
 * 手動出單：把訂單收據排入 printer_jobs（CloudPRNT / Bridge 取件）
 * @returns {Promise<{queued: number, reason?: string}>}
 */
export async function printOrder(orderId) {
  return request('/orders-print', {
    method: 'POST',
    body: { order_id: orderId },
  });
}

// ─────────── Workflow ───────────
/**
 * 取得 tenant workflow（含 states + transitions）
 * @param {Object} opts
 * @param {number} [opts.ifNoneMatch] — 用 workflow.version 比對，304 回 { __not_modified: true }
 */
export async function fetchWorkflow({ ifNoneMatch } = {}) {
  const headers = {};
  if (ifNoneMatch != null) headers['If-None-Match'] = `W/"${ifNoneMatch}"`;
  const supabase = getSupabase();
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // 2026-04-23 UAT fix：cache: 'no-store' 繞 browser HTTP cache，確保每次都
  // hit server 做 ETag 驗證（304 時 server 仍快速回，但不會拿 browser stale cache）
  const res = await fetch(apiUrl('/.netlify/functions/workflow-list'), { headers, cache: 'no-store' });
  if (res.status === 304) return { __not_modified: true };
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

// ─────────── 小掌櫃（內部代號 小紘）AI 對話 ───────────
/**
 * 問小掌櫃（chat 對話）
 * @param {Object} opts
 * @param {string} opts.question
 * @param {string} [opts.session_id]
 * @returns {Promise<{answer, session_id, model, latency_ms, key_source, tool_calls}>}
 */
export async function askXiaohong({ question, session_id } = {}) {
  return request('/ai-xiaohong', {
    method: 'POST',
    body: { question, session_id },
  });
}

// ─────────── 小掌櫃（內部代號 小暖）行銷文案審核 ───────────
/**
 * 列文案草稿（單一日期或近 N 天）
 * @param {Object} [opts]
 * @param {string} [opts.date]    YYYY-MM-DD
 * @param {number} [opts.days]    1-30；若給則用近 N 天，與 date 二擇一
 * @param {'draft'|'approved'|'rejected'|'posted'} [opts.status]
 */
export async function listMarketingDrafts({ date, days, status } = {}) {
  const qs = new URLSearchParams();
  if (date) qs.set('date', date);
  if (days) qs.set('days', String(days));
  if (status) qs.set('status', status);
  const path = qs.toString() ? `/ai-xiaonuan-drafts?${qs}` : '/ai-xiaonuan-drafts';
  return request(path, { method: 'GET' });
}

/**
 * 對草稿做動作（批准 / 拒絕 / 編輯後批准 / 標記已貼出）
 * @param {Object} opts
 * @param {string} opts.id
 * @param {'approve'|'reject'|'edit_and_approve'|'mark_posted'} opts.action
 * @param {string} [opts.edited_copy_text]   action='edit_and_approve' 時必填
 */
export async function actMarketingDraft({ id, action, edited_copy_text } = {}) {
  return request('/ai-xiaonuan-draft-action', {
    method: 'POST',
    body: { id, action, edited_copy_text },
  });
}

// ─────────── BYOK AI keys（owner only）───────────
/**
 * 列目前啟用的 AI keys（不含明文）
 * @returns {Promise<{keys: Array}>}
 */
export async function listAiKeys() {
  return request('/ai-keys', { method: 'GET' });
}

/**
 * 新增 / 替換 AI key（server 會 live validate）
 * @param {Object} opts
 * @param {'gemini'|'anthropic'|'groq'|'openai'} opts.provider
 * @param {string} opts.api_key
 * @param {'primary'|'fallback'} [opts.key_slot]  default 'primary'
 * @param {number} [opts.daily_tokens_limit]
 */
export async function saveAiKey({ provider, api_key, key_slot = 'primary', daily_tokens_limit } = {}) {
  return request('/ai-keys', {
    method: 'POST',
    body: { provider, api_key, key_slot, daily_tokens_limit },
  });
}

/**
 * 停用某 provider/slot 的 key
 * @param {string} provider
 * @param {'primary'|'fallback'} [slot]  default 'primary'
 */
export async function deleteAiKey(provider, slot = 'primary') {
  const qs = new URLSearchParams({ provider, key_slot: slot }).toString();
  return request(`/ai-keys?${qs}`, { method: 'DELETE' });
}

// ─────────── 小倉效期驅動推薦（dashboard widget）───────────
/**
 * 撈某日的「今日建議推銷」清單（小倉 03:00 批次輸出）
 * @param {Object} [opts]
 * @param {string} [opts.date]  YYYY-MM-DD，省略則用 tenant 時區「今天」
 * @returns {Promise<{for_date, item_count, items, generated_by, model, created_at}>}
 */
export async function fetchTodayRecommendations({ date } = {}) {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  try {
    return await request(`/daily-push-recommendations${qs}`);
  } catch (err) {
    console.warn('[api] fetchTodayRecommendations failed, returning empty', err);
    return { for_date: null, item_count: 0, items: [], generated_by: null, model: null, created_at: null };
  }
}

// ─────────── Lighthouse 燈塔 key 兌換（owner only）───────────
/**
 * 兌換燈塔終身 key
 * @param {string} code 例：LH-XXXX-XXXX-XXXX-XXXX
 * @returns {Promise<{success: true, program: string, perk: string, refresh_session: boolean}>}
 */
export async function redeemLighthouseCode(code) {
  return request('/lighthouse-redeem', {
    method: 'POST',
    body: { code },
    idempotencyKey: crypto.randomUUID(),
  });
}

// ─────────── Reports ───────────
/**
 * 營收報表
 * @param {'day'|'month'|'year'} period
 * @param {string} date YYYY-MM-DD
 * @param {string} [storeId]
 */
export async function fetchRevenueReport({ period, date, storeId } = {}) {
  const params = new URLSearchParams({ period, date });
  if (storeId) params.set('store_id', storeId);
  return request(`/reports-revenue?${params}`);
}

// ─────────── Payment ───────────
export async function createCheckout(orderId, method = 'ecpay') {
  return request('/payment-create', {
    method: 'POST',
    body: { order_id: orderId, method },
  });
}

/**
 * LINE Pay 建立交易（回 payment_url 供 redirect；實扣款在 server confirm）
 * @param {string} orderId
 * @returns {Promise<{method:string, transaction_id:string, payment_url:{web:string, app:string}}>}
 */
export async function createLinePayRequest(orderId) {
  return request('/payment-linepay-request', {
    method: 'POST',
    body: { order_id: orderId },
  });
}

// ─────────── Payment Credentials（BYOK 商家自有金流帳號，owner only）───────────

/** 列金流憑證狀態（只回 hint，永不回明文） */
export async function listPaymentCredentials() {
  return request('/payment-credentials');
}

/**
 * 設定金流憑證（加密儲存）
 * @param {'ecpay'|'linepay'} provider
 * @param {object} credentials ecpay: {merchant_id, hash_key, hash_iv} / linepay: {channel_id, channel_secret}
 */
export async function setPaymentCredentials(provider, credentials) {
  return request('/payment-credentials', {
    method: 'POST',
    body: { provider, credentials },
  });
}

/** 移除（停用）金流憑證 */
export async function deletePaymentCredentials(provider) {
  return request(`/payment-credentials?provider=${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });
}

// ─────────── Reservations（W9 提早 MVP）───────────

/**
 * Guest 訂位（不需登入）
 * @param {object} p
 * @param {string} p.slug
 * @param {string} p.reservedAt - ISO timestamp
 * @param {number} p.partySize
 * @param {string} p.guestName
 * @param {string} p.guestPhone
 * @param {string} [p.guestEmail]
 * @param {string} [p.note]
 * @param {number} [p.durationMinutes]
 */
export async function createPublicReservation({ slug, reservedAt, partySize, guestName, guestPhone, guestEmail, note, durationMinutes }) {
  const res = await fetchWithRetry(apiUrl('/.netlify/functions/reservation-create'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': crypto.randomUUID(),
    },
    body: JSON.stringify({
      slug,
      reserved_at: reservedAt,
      party_size: partySize,
      guest_name: guestName,
      guest_phone: guestPhone,
      guest_email: guestEmail,
      note,
      duration_minutes: durationMinutes,
      source: 'web',
    }),
  }, WRITE_RETRY_STATUS);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

/** Owner：取訂位設定 */
export async function fetchReservationConfig({ storeId }) {
  return request(`/reservation-config-get?store_id=${encodeURIComponent(storeId)}`);
}

/** Owner：upsert 訂位設定 */
export async function upsertReservationConfig(payload) {
  return request('/reservation-config-upsert', {
    method: 'POST',
    body: payload,
    idempotencyKey: crypto.randomUUID(),
  });
}

/** Guest：拉某日 + 人數對應的可訂位時段（公開 endpoint，無需登入） */
export async function fetchReservationAvailability({ slug, date, partySize }) {
  const params = new URLSearchParams({ slug, party_size: String(partySize || 2) });
  if (date) params.set('date', date);   // 省略 date = 只拿 config（月曆初始化）
  const res = await fetch(apiUrl(`/.netlify/functions/reservation-availability?${params}`));
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError({ ...data.error, status: res.status });
  return data;
}

/** 員工：取某日訂位清單（默認今天） */
export async function listReservations({ date, status, scope } = {}) {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (status) params.set('status', status);
  if (scope) params.set('scope', scope);
  const qs = params.toString() ? `?${params}` : '';
  return request(`/reservation-list${qs}`);
}

/** 員工：改訂位狀態 */
export async function updateReservationStatus({ reservationId, status, cancelReason, tableId } = {}) {
  return request('/reservation-update-status', {
    method: 'POST',
    body: {
      reservation_id: reservationId,
      status,
      cancel_reason: cancelReason,
      table_id: tableId,
    },
    idempotencyKey: crypto.randomUUID(),
  });
}

// ─────────── 排班（W11 MVP）───────────

/**
 * Owner：AI 排班生成（Layer 0 需求預測 + solver → draft schedule）
 * @param {object} opts
 * @param {string} opts.periodStart 週期起始日 YYYY-MM-DD（週一）
 * @param {string} [opts.storeId]
 */
export async function generateSchedule({ periodStart, storeId } = {}) {
  return request('/scheduling-generate', {
    method: 'POST',
    body: { period_start: periodStart, store_id: storeId },
    idempotencyKey: crypto.randomUUID(),
  });
}

export { ApiError };
